import { INPUTS_TRADE_SEEDS } from '../../Pages/OrgSettings/InputsTradeGroupsData';

export type ScanTemplateTradeSettings = {
  displayName?: string;
  isEnabled?: boolean;
};

export type ScanTemplateTradeOption = {
  value: string;
  label: string;
};

const DEFAULT_TRADE_OPTIONS: ScanTemplateTradeOption[] = INPUTS_TRADE_SEEDS.map(
  trade => ({ value: trade.slug, label: trade.displayName }),
);

const DEFAULT_TRADE_LABELS_BY_SLUG = Object.fromEntries(
  INPUTS_TRADE_SEEDS.map(trade => [trade.slug, trade.displayName]),
);

const getNormalizedTrade = (trade?: string | null): string | undefined => trade?.trim() || undefined;

const getTradeLabel = (
  tradeSlug: string,
  configuredTrades?: Record<string, ScanTemplateTradeSettings>,
): string =>
  configuredTrades?.[tradeSlug]?.displayName
  || DEFAULT_TRADE_LABELS_BY_SLUG[tradeSlug]
  || tradeSlug;

const getCurrentTradeOption = (
  currentTrade: string,
  configuredTrades?: Record<string, ScanTemplateTradeSettings>,
): ScanTemplateTradeOption => ({
  value: currentTrade,
  label: getTradeLabel(currentTrade, configuredTrades),
});

/**
 * Builds the task-trade dropdown for the scan-template editor.
 *
 * Disabled trades stay hidden for new assignments, but an already-assigned trade
 * remains selectable so existing tasks do not lose their visible value.
 */
export const getScanTemplateTradeOptions = ({
  configuredTrades,
  currentTrade,
}: {
  configuredTrades?: Record<string, ScanTemplateTradeSettings>;
  currentTrade?: string | null;
}): ScanTemplateTradeOption[] => {
  const normalizedCurrentTrade = getNormalizedTrade(currentTrade);

  if (!configuredTrades || Object.keys(configuredTrades).length === 0) {
    return normalizedCurrentTrade
        && !DEFAULT_TRADE_OPTIONS.some(({ value }) => value === normalizedCurrentTrade)
      ? [...DEFAULT_TRADE_OPTIONS, getCurrentTradeOption(normalizedCurrentTrade)]
      : DEFAULT_TRADE_OPTIONS;
  }

  const configuredTradeOptions = Object.entries(configuredTrades)
    .filter(([tradeSlug, trade]) => trade.isEnabled !== false || tradeSlug === normalizedCurrentTrade)
    .map(([tradeSlug]) => getCurrentTradeOption(tradeSlug, configuredTrades));

  return normalizedCurrentTrade
      && !configuredTradeOptions.some(({ value }) => value === normalizedCurrentTrade)
    ? [...configuredTradeOptions, getCurrentTradeOption(normalizedCurrentTrade, configuredTrades)]
    : configuredTradeOptions;
};
